import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { WABanner } from "@/components/layout/WABanner";
import { POSVendaDireta as VendaDireta } from "@/components/pos/POSVendaDireta";
import POSHome from "@/components/pos/POSHome";
import AbrirCaixaView from "@/components/pos/AbrirCaixaView";
import FecharCaixaView from "@/components/pos/FecharCaixaView";
import ContasView from "@/components/pos/ContasView";
import ContaMesaView from "@/components/pos/ContaMesaView";
import PagamentosView from "@/components/pos/PagamentosView";
import PedidosView from "@/components/pos/PedidosView";
import AutoatendimentoView from "@/components/pos/AutoatendimentoView";
import POSConfigView from "@/components/pos/POSConfigView";
import RecibosView from "@/components/pos/RecibosView";
import GuestCountModal from "@/components/pos/GuestCountModal";
import SessionExpiredModal from "@/components/pos/SessionExpiredModal";

// ====== MAIN POS APP ======
export default function POSPage({ isTerminalMode = false }) {
  const { authAxios, user } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [screen, setScreen] = useState("home");
  const [selectedTable, setSelectedTable] = useState(null);
  const [caixaAberto, setCaixaAberto] = useState(true);
  const [caixaRequiresClose, setCaixaRequiresClose] = useState(false);
  const [clienteOrderSource, setClienteOrderSource] = useState("counter"); // whatsapp | counter | platform
  const [showGuestCountModal, setShowGuestCountModal] = useState(false);
  const [guestCheckLoading, setGuestCheckLoading] = useState(false);

  // Check if caixa is open on mount
  useEffect(() => {
    authAxiosRef.current.get("/pos/caixa/status")
      .then(r => {
        setCaixaAberto(r.data?.aberto ?? true);
        setCaixaRequiresClose(r.data?.requires_close ?? false);
      })
      .catch(() => {
        setCaixaAberto(true); // Assume open if endpoint doesn't exist
        setCaixaRequiresClose(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Terminal mode: prevent accidental navigation, add class for tablet CSS
  useEffect(() => {
    if (isTerminalMode) {
      document.body.classList.add('pos-terminal-mode');
      return () => document.body.classList.remove('pos-terminal-mode');
    }
  }, [isTerminalMode]);

  const handleGuestCountConfirm = async (guestCount) => {
    if (!selectedTable) return false;

    try {
      // Save to table_guests collection
      await authAxiosRef.current.post(`/tables/${selectedTable.id}/guests`, { guests: guestCount });
      // Update selected table state with guest count
      setSelectedTable(prev => ({ ...prev, guestCount }));
      // Close modal and proceed to venda_mesa screen
      setShowGuestCountModal(false);
      setScreen("venda_mesa");
      return true;
    } catch (err) {
      console.error("Error saving guests:", err);
      throw new Error(err.response?.data?.detail || "Error al guardar cantidad de personas");
    }
  };

  const handleSelectTable = async (table) => {
    console.log("[POS] handleSelectTable called with table:", table);
    const t = { ...table, name: table.name || `MESA ${String(table.number).padStart(2, '0')}` };
    setSelectedTable(t);
    console.log("[POS] Table is occupied?", table.isOccupied, table.status, table.has_open_order);
    if (table.isOccupied || table.status === "occupied" || table.has_open_order) {
      console.log("[POS] Table is occupied → going to conta_mesa");
      setScreen("conta_mesa");
    } else {
      console.log("[POS] Table is available → checking for existing guests record");
      // Available table → check if table_guests record exists
      setGuestCheckLoading(true);
      try {
        console.log("[POS] Fetching GET /tables/", table.id, '/account');
        const response = await authAxiosRef.current.get(`/tables/${table.id}/account`);
        const guests = response.data?.guests;
        console.log("[POS] Response guests:", guests, "Full response:", response.data);

        if (guests) {
          console.log("[POS] Guests record exists (", guests, ') → skip modal, go to venda_mesa');
          // Record exists → skip modal, proceed directly to VendaDireta
          setSelectedTable(prev => ({ ...prev, guestCount: guests }));
          setScreen("venda_mesa");
        } else {
          console.log("[POS] No guests record → SHOWING MODAL");
          // No record → show modal (required, once only)
          setShowGuestCountModal(true);
        }
      } catch (err) {
        console.error("[POS] Error checking guests:", err);
        console.log("[POS] Error → showing modal as fail-safe");
        // On error, show modal to ask for guests (fail-safe)
        setShowGuestCountModal(true);
      } finally {
        setGuestCheckLoading(false);
      }
    }
  };

  const handleRefreshCaixaStatus = async () => {
    try {
      const r = await authAxiosRef.current.get("/pos/caixa/status");
      setCaixaAberto(r.data?.aberto ?? true);
      setCaixaRequiresClose(r.data?.requires_close ?? false);
    } catch (err) {
      console.error("Error refreshing caixa status:", err);
    }
  };

  const handleCaixaFechado = () => {
    setCaixaRequiresClose(false);
    setScreen("abrir_caixa");
  };

  const renderScreen = () => {
    switch (screen) {
      case "abrir_caixa":
        return <AbrirCaixaView authAxios={authAxios} onBack={() => setScreen("home")} onCaixaAberto={() => { setCaixaAberto(true); setCaixaRequiresClose(false); setScreen("home"); }} />;
      case "fechar_caixa":
        return <FecharCaixaView authAxios={authAxios} onBack={() => setScreen("home")} onCaixaFechado={handleCaixaFechado} />;
      case "venda":
        return <VendaDireta authAxios={authAxios} onBack={() => setScreen("home")} />;
      case "pedido_balcao": // kept for backwards compat → same as novo_pedido
        return <VendaDireta authAxios={authAxios} onBack={() => setScreen("home")} requireCustomer={true} orderSource={clienteOrderSource} />;
      case "venda_mesa":
        return <VendaDireta authAxios={authAxios}
          onBack={() => selectedTable ? setScreen("conta_mesa") : setScreen("contas")}
          tableNumber={selectedTable?.number} tableName={selectedTable?.name} isTableOrder={true}
          guestCount={selectedTable?.guestCount}
        />;
      case "conta_mesa":
        return <ContaMesaView
          authAxios={authAxios}
          table={selectedTable}
          onBack={() => setScreen("contas")}
          onAddMore={() => setScreen("venda_mesa")}
        />;
      case "contas":
        return <ContasView authAxios={authAxios} onBack={() => setScreen("home")} onSelectTable={handleSelectTable} onClienteOrder={(src) => { setClienteOrderSource(src || "counter"); setScreen("pedido_balcao"); }} />;
      case "abrir_conta":
        return <ContasView authAxios={authAxios} onBack={() => setScreen("home")} onSelectTable={handleSelectTable} onClienteOrder={(src) => { setClienteOrderSource(src || "counter"); setScreen("pedido_balcao"); }} />;
      case "novo_pedido":
        return <ContasView authAxios={authAxios} onBack={() => setScreen("home")} onSelectTable={handleSelectTable} onClienteOrder={(src) => { setClienteOrderSource(src || "counter"); setScreen("pedido_balcao"); }} />;
      case "pagamentos":
        return <PagamentosView authAxios={authAxios} onBack={() => setScreen("home")} />;
      case "pedidos":
        return <PedidosView authAxios={authAxios} onBack={() => setScreen("home")} />;
      case "recibos":
        return <RecibosView authAxios={authAxios} onBack={() => setScreen("home")} />;
      case "auto":
        return <AutoatendimentoView authAxios={authAxios} onBack={() => setScreen("home")} restaurantName={user?.restaurant_name} />;
      case "config":
        return <POSConfigView authAxios={authAxios} onBack={() => setScreen("home")} />;
      default:
        return <POSHome onNavigate={setScreen} restaurantName={user?.restaurant_name || "KAISO POS"} caixaAberto={caixaAberto} />;
    }
  };

  if (caixaRequiresClose && screen !== "fechar_caixa") {
    return <SessionExpiredModal onFechar={() => setScreen("fechar_caixa")} />;
  }

  return (
    <>
      <WABanner />
      {showGuestCountModal && (
        <GuestCountModal
          open={showGuestCountModal}
          onOpenChange={setShowGuestCountModal}
          table={selectedTable}
          onConfirm={handleGuestCountConfirm}
          isLoading={guestCheckLoading}
        />
      )}
      {!showGuestCountModal && (
        <>
          {renderScreen()}
        </>
      )}
    </>
  );
}
